import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';

const ScheduleDisplay = () => {
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [assignments, setAssignments] = useState({});
  const [vehicleAssignments, setVehicleAssignments] = useState({});
  const [beaches, setBeaches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch beaches
  const fetchBeaches = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/beaches');
      // Load ALL beaches, not just active ones, so we can display assignments for closed beaches
      const allBeaches = response.data;
      // Sort beaches by sort_order
      allBeaches.sort((a, b) => a.sort_order - b.sort_order);
      setBeaches(allBeaches);
    } catch (error) {
      console.error('Failed to fetch beaches:', error);
      setError('Failed to load beaches');
    }
  }, []);

  // Load schedule assignments for the selected date
  const loadAssignments = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiClient.get(`/api/schedule-assignments/${selectedDate}`);
      
      if (response.data.assignments && Object.keys(response.data.assignments).length > 0) {
        setAssignments(response.data.assignments);
        setVehicleAssignments(response.data.vehicleAssignments || {});
      } else {
        setAssignments({});
        setVehicleAssignments({});
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setAssignments({});
        setVehicleAssignments({});
      } else {
        console.error('Failed to load assignments:', error);
        setError('Failed to load schedule assignments');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  // Load data on mount and date change
  useEffect(() => {
    const loadData = async () => {
      await fetchBeaches();
      await loadAssignments();
    };
    loadData();
  }, [fetchBeaches, loadAssignments]);

  // Date navigation functions
  const goToPreviousDay = () => {
    const [year, month, day] = selectedDate.split('-').map(Number);
    const currentDate = new Date(year, month - 1, day - 1);
    const newDateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
    setSelectedDate(newDateStr);
  };

  const goToNextDay = () => {
    const [year, month, day] = selectedDate.split('-').map(Number);
    const currentDate = new Date(year, month - 1, day + 1);
    const newDateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
    setSelectedDate(newDateStr);
  };

  const goToToday = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    setSelectedDate(`${year}-${month}-${day}`);
  };

  // Format date for display
  const formatDisplayDate = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const today = new Date();
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth();
    const todayDay = today.getDate();
    const tomorrow = new Date(todayYear, todayMonth, todayDay + 1);
    const yesterday = new Date(todayYear, todayMonth, todayDay - 1);

    // Check if it's today, tomorrow, or yesterday
    if (date.toDateString() === today.toDateString()) {
      return `Today - ${date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })}`;
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return `Tomorrow - ${date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday - ${date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
  };

  // Get display name for employee
  const getDisplayName = (employee) => {
    const firstName = employee.first_name || '';
    const lastName = employee.last_name || '';
    const username = employee.username || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (username) {
      return username;
    } else {
      return 'Unknown User';
    }
  };

  // Get rank color
  const getRankColor = (rank) => {
    const colors = {
      'Captain': '#dc2626',
      'Lieutenant': '#ea580c',
      'Sergeant': '#ca8a04',
      'Lifeguard': '#16a34a',
      'Rookie': '#2563eb'
    };
    return colors[rank] || '#6b7280';
  };

  // Calculate total assignments
  const totalAssignments = Object.values(assignments).reduce((sum, beachEmployees) => sum + beachEmployees.length, 0);
  const totalVehicleAssignments = Object.keys(vehicleAssignments).length;

  return (
    <div className="schedule-display">
      <div className="display-header">
        <h1>Schedule Display</h1>
        
        <div className="date-navigation">
          <button 
            className="btn btn-secondary"
            onClick={goToPreviousDay}
            title="Previous Day"
          >
            ← Previous
          </button>
          
          <div className="current-date">
            <h2>{formatDisplayDate(selectedDate)}</h2>
          </div>
          
          <button 
            className="btn btn-secondary"
            onClick={goToNextDay}
            title="Next Day"
          >
            Next →
          </button>
        </div>

        <button 
          className="btn btn-primary"
          onClick={goToToday}
        >
          Today
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      <div className="schedule-summary">
        <div className="summary-stats">
          <div className="stat">
            <div className="stat-number">{totalAssignments}</div>
            <div className="stat-label">Total Assignments</div>
          </div>
          <div className="stat">
            <div className="stat-number">{totalVehicleAssignments}</div>
            <div className="stat-label">Vehicle Assignments</div>
          </div>
          <div className="stat">
            <div className="stat-number">{beaches.filter(beach => beach.is_active).length}</div>
            <div className="stat-label">Active Beaches</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading schedule...</p>
        </div>
      ) : totalAssignments === 0 ? (
        <div className="no-assignments">
          <h3>No Schedule Available</h3>
          <p>No assignments have been made for {formatDisplayDate(selectedDate)}.</p>
        </div>
      ) : (
        <div className="assignments-display">
          <div className="beaches-display-grid">
            {beaches.map(beach => {
              const beachEmployees = assignments[beach.id] || [];
              
              if (beachEmployees.length === 0) return null;

              return (
                <div key={beach.id} className="beach-display-card">
                  <div className="beach-display-header">
                    <h3>{beach.name}</h3>
                    {beach.description && (
                      <p className="beach-description">{beach.description}</p>
                    )}
                    <div className="employee-count">
                      {beachEmployees.length} {beachEmployees.length === 1 ? 'Employee' : 'Employees'}
                    </div>
                  </div>
                  
                  <div className="beach-employees-display">
                    {beachEmployees.map((employee, index) => {
                      const assignedVehicle = vehicleAssignments[employee.id];
                      
                      return (
                        <div 
                          key={employee.id} 
                          className="employee-display-card"
                          style={{ borderLeft: `4px solid ${getRankColor(employee.rank)}` }}
                        >
                          <div className="employee-display-info">
                            <div className="employee-display-name">
                              {getDisplayName(employee)}
                            </div>
                            <div className="employee-display-rank">
                              {employee.rank}
                            </div>
                            <div className="employee-display-details">
                              {employee.years_of_service > 0 && (
                                <span className="years-badge">{employee.years_of_service} years</span>
                              )}
                              {employee.emt_training && (
                                <span className="badge badge-success">EMT</span>
                              )}
                              {employee.advanced_training && (
                                <span className="badge badge-info">Advanced</span>
                              )}
                              {employee.beach && (
                                <span className="preferred-beach">Prefers: {employee.beach}</span>
                              )}
                            </div>
                            {assignedVehicle && (
                              <div className="vehicle-display">
                                🚗 {assignedVehicle.vehicle.name}
                                {assignedVehicle.vehicle.description && (
                                  <span className="vehicle-desc"> - {assignedVehicle.vehicle.description}</span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="position-number">
                            #{index + 1}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleDisplay; 